import type {
  OperationVariables,
  QueryHookOptions,
  TypedDocumentNode,
} from '@apollo/client';
import { useCallback, useMemo, useState } from 'react';

import { useFhirQuery } from '@eluve/fhir-renderer';

import { type FhirReadError, classifyReadError } from './read-error';

/**
 * FHIR data-fetching primitives shared by feature libs under
 * libs/frontend/fhir/. The renderer itself stays fetch-agnostic (its render
 * roots take `data`, not queries) — this lib is where the querying
 * conventions live:
 * - each feature lib owns a fhirQueries.ts of gql.tada documents whose
 *   selections are its display contract;
 * - list reads use Medplum's Connection API through useFhirConnection, which
 *   pages only on an explicit loadMore() (LoadMoreRow is the affordance) —
 *   never an unbounded fetch-all loop;
 * - writes and `_history` stay on the Medplum SDK/REST client (GraphQL is
 *   the read path).
 */

export type FhirQueryState<T> = {
  data: T | null;
  loading: boolean;
  failed: boolean;
  /**
   * Classified failure, set whenever the query errored — even when partial
   * data came back (errorPolicy 'all'), which is how a per-field AccessPolicy
   * denial arrives. Feed with `data` through toSectionStatus to tell
   * restricted apart from empty.
   */
  readError: FhirReadError | null;
  refetch: () => void;
};

/**
 * Paging surface of a Connection-backed read. Fetching is bounded: exactly
 * one page per explicit loadMore() call — the affordance, not an auto-drain
 * loop, decides how much of a large dataset to pull.
 *
 * Caveats for consumers:
 * - loaded/count speak raw connection rows, which can differ from mapped
 *   items (e.g. roles fetched vs practitioners after dedupe);
 * - refetch() resets to the first page, collapsing loaded pages;
 * - a write that fans out over the full dataset must guard on hasMore, or
 *   it silently operates on the loaded prefix only.
 */
export type FhirPageControls = {
  /** Rows fetched so far (raw connection edges, before any mapping/dedupe). */
  loaded: number;
  /** Server-side total across all pages; null until the first page lands. */
  count: number | null;
  hasMore: boolean;
  /** Appends the next page. No-op while loading or when nothing remains. */
  loadMore: () => void;
  loadingMore: boolean;
};

export type FhirConnectionState<T> = FhirQueryState<T> & FhirPageControls;

/**
 * Adapts a plain (non-paged) FHIR GraphQL read into the simple query-state
 * shape. `map` must be referentially stable (module-level function or
 * useCallback) — it feeds a useMemo over the raw payload.
 */
export function useFhirData<TData, TVariables extends OperationVariables, T>(
  query: TypedDocumentNode<TData, TVariables>,
  map: (data: TData) => T,
  options?: QueryHookOptions<TData, TVariables>,
): FhirQueryState<T> {
  const { data, loading, error, refetch, enabled } = useFhirQuery(
    query,
    options,
  );
  const mapped = useMemo(
    () => (data === undefined ? null : map(data)),
    [data, map],
  );
  // Guarded: Apollo executes refetch() even on a skipped query, and a query
  // skipped for a missing FHIR client would run against the app's default
  // (Hasura) Apollo client. Dropping the call is safe — once the client
  // arrives the initial query fires anyway.
  const doRefetch = useCallback(() => {
    if (enabled) void refetch();
  }, [refetch, enabled]);
  return {
    data: mapped,
    // !enabled = the FHIR client is still initializing. Consumers are
    // MEDPLUM-gated routes, so it always arrives — surface as loading, not
    // empty.
    loading: !enabled || (loading && data === undefined),
    failed: Boolean(error) && data === undefined,
    readError: error ? classifyReadError(error) : null,
    refetch: doRefetch,
  };
}

/** The shape every Medplum `XxxConnection` selection shares. */
type FhirConnection = {
  count?: number | null;
  offset?: number | null;
  edges?: readonly (object | null)[] | null;
} | null;

/**
 * useFhirData plus explicit paging for a query whose single root field is a
 * Medplum Connection (`field`). loadMore() appends the next `_offset` page
 * into the same Apollo cache entry, so `map` always sees the merged payload.
 * refetch() intentionally resets to the first page (original variables).
 * Screens listing several connections compose one hook per document — a
 * combined multi-root document can't page its lists independently.
 */
export function useFhirConnection<
  K extends string,
  TData extends Partial<Record<K, FhirConnection>>,
  TVariables extends OperationVariables,
  T,
>(
  query: TypedDocumentNode<TData, TVariables>,
  field: K,
  map: (data: TData) => T,
  options?: QueryHookOptions<TData, TVariables>,
): FhirConnectionState<T> {
  const { data, loading, error, refetch, fetchMore, enabled } = useFhirQuery(
    query,
    options,
  );
  const [loadingMore, setLoadingMore] = useState(false);

  const mapped = useMemo(
    () => (data === undefined ? null : map(data)),
    [data, map],
  );
  // Same skipped-refetch guard as useFhirData (see comment there).
  const doRefetch = useCallback(() => {
    if (enabled) void refetch();
  }, [refetch, enabled]);

  const connection = data?.[field];
  const loaded = connection?.edges?.length ?? 0;
  const count = connection?.count ?? null;
  const hasMore = count !== null && loaded < count;

  const loadMore = useCallback(() => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    fetchMore({
      variables: { offset: loaded } as unknown as Partial<TVariables>,
      updateQuery: (prev, { fetchMoreResult }) => {
        // Apollo widens prev/fetchMoreResult to Unmasked<TData>, which drops
        // the Record<K, FhirConnection> constraint — re-narrow to TData so the
        // connection field can be indexed by K.
        const prevData = prev as unknown as TData;
        const nextData = fetchMoreResult as unknown as TData;
        const prevConn = prevData[field];
        const nextConn = nextData[field];
        if (!nextConn) return prev;
        return {
          ...nextData,
          [field]: {
            ...nextConn,
            edges: [...(prevConn?.edges ?? []), ...(nextConn.edges ?? [])],
          },
        } as unknown as typeof prev;
      },
    })
      // Failure leaves the loaded pages intact; the button stays for a retry.
      .catch(() => undefined)
      .finally(() => setLoadingMore(false));
  }, [fetchMore, field, hasMore, loadingMore, loaded]);

  return {
    data: mapped,
    loading: !enabled || (loading && data === undefined),
    failed: Boolean(error) && data === undefined,
    readError: error ? classifyReadError(error) : null,
    refetch: doRefetch,
    loaded,
    count,
    hasMore,
    loadMore,
    loadingMore,
  };
}
